import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type {
  DrawLottery, NumberLimitHistoryActors, NumberLimitHistoryEntry, NumberLimitHistoryQuery,
  PaginatedResult,
} from '@nexbit/types'
import { ApiRequestError } from '@/lib/api'
import {
  ACTION_LABEL, actionVariant, betTypeLabel, changeLabel, ictDaysAgo, ictEndExclusive,
  ictStart, isNoOp, numberLabel, parseSetBy, scopeLabel, setByOptions, summarize, todayIct,
  whoseBook,
} from '@/lib/number-limit-history'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertCircle, ChevronLeft, ChevronRight, Eye, History, Search, X,
} from 'lucide-react'

/**
 * ประวัติการอั้นเลข — the table both consoles show.
 *
 * Shared because the admin's view and an agent's view of its own rules are the same
 * question asked with a different reach, and two copies of a table whose whole job is to
 * be trusted would drift. What differs is passed in: the admin gets the หวย and ขอบเขต
 * filters and a column saying whose book each change applied to; the agent's is already
 * one agent and one book, so those would be a column of the same value.
 *
 * Not polled. The other number screens refresh every five seconds because a ceiling fills
 * while you watch it; this one is a record of the past, and a list that reshuffled under
 * the cursor every five seconds would be unreadable at exactly the moment somebody is
 * trying to read it carefully.
 */

const PAGE_SIZE = 50

interface Props {
  /** Where the rows come from. The agent's is scoped server-side from its session. */
  fetchPage: (q: NumberLimitHistoryQuery) => Promise<PaginatedResult<NumberLimitHistoryEntry>>
  /** Offered in the หวย filter. Omit to leave the filter out. */
  lotteries?: DrawLottery[]
  /** The admin's view: show whose book, and allow filtering by it. */
  showScope?: boolean
  /** Who to offer in the ตั้งโดย picker. Only read when showScope is on. */
  actors?: NumberLimitHistoryActors | null
  /** Shown above the table. */
  subtitle: string
  emptyHint: string
}

/** The filters as the inputs hold them, before a search is pressed. */
interface Draft {
  from: string
  to: string
  number: string
  lotteryId: string
  /** The ตั้งโดย picker's raw value — see parseSetBy for what each form means. */
  setBy: string
}

/** The window the screen opens on: the last seven Thai days, today included. */
function initialDraft(): Draft {
  return { from: ictDaysAgo(7), to: todayIct(), number: '', lotteryId: '', setBy: '' }
}

function toQuery(d: Draft): NumberLimitHistoryQuery {
  return {
    limit: PAGE_SIZE,
    ...(d.from ? { from: ictStart(d.from) } : {}),
    ...(d.to ? { to: ictEndExclusive(d.to) } : {}),
    ...(d.number.trim() ? { number: d.number.trim() } : {}),
    ...(d.lotteryId ? { lotteryId: d.lotteryId } : {}),
    ...parseSetBy(d.setBy),
  }
}

/**
 * `block` is not decoration: a native <select> is inline-block, so a <label> above it
 * sits beside it instead, and the two filters built on a select had their labels pushed
 * onto the same line while the one built on <Input> stacked correctly.
 */
const SELECT_CLASS =
  'block h-9 rounded-[var(--radius-sm)] border border-input bg-background px-2.5 pr-8'
  + ' text-sm text-foreground'
  + ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

/**
 * One target's change, as a chip. Greyed when the save did not move anything.
 *
 * `highlight` marks the number that was searched for. One save can touch thirty numbers,
 * so an entry comes back for a search on 123 while showing 456 next to it — correct,
 * because the entry is one decision and hiding the rest would misreport it, but the eye
 * needs telling which one it asked about.
 */
function ChangeChip({
  change, highlight,
}: { change: NumberLimitHistoryEntry['changes'][number]; highlight?: string }) {
  const noOp = isNoOp(change)
  // Lower-cased on both sides: the server stores 'high' and normalises the search the
  // same way, so a search typed as 'HIGH' must still light up the row it returned.
  const matched = !!highlight && change.number?.toLowerCase() === highlight.toLowerCase()
  return (
    <div className={noOp ? 'text-muted-foreground/60' : undefined}>
      <span className="text-[11px] text-muted-foreground">{betTypeLabel(change.betType)}</span>
      <span
        className={`font-mono text-xs font-semibold ml-1.5 ${
          matched
            ? 'text-primary bg-primary/10 rounded-[var(--radius-sm)] px-1 -mx-0.5'
            : 'text-foreground'
        }`}
      >
        {numberLabel(change.number)}
      </span>
      <span className="font-mono text-[11px] ml-1.5">{changeLabel(change)}</span>
      {noOp && <span className="text-[10px] ml-1.5">(ไม่เปลี่ยน)</span>}
    </div>
  )
}

function DetailDialog({
  entry, onClose, showScope, highlight,
}: {
  entry: NumberLimitHistoryEntry | null
  onClose: () => void
  showScope: boolean
  highlight?: string
}) {
  return (
    <Dialog open={!!entry} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {entry ? ACTION_LABEL[entry.action] : 'รายละเอียด'}
          </DialogTitle>
        </DialogHeader>
        {entry && (
          <div className="space-y-3 text-xs">
            <dl className="space-y-1.5">
              <Field label="เวลา" value={fullTime(entry.createdAt)} />
              <Field label="ผู้ทำ" value={entry.actorUsername ?? entry.actorId ?? '(ระบบ)'} />
              {showScope && <Field label="มีผลกับ" value={whoseBook(entry)} />}
              <Field
                label="หวย"
                value={entry.lotteryName ?? entry.lotteryCode ?? 'ไม่ทราบ (บันทึกไว้ไม่ครบ)'}
              />
              <Field label="ขอบเขต" value={scopeLabel(entry)} />
              {entry.note && <Field label="หมายเหตุ" value={entry.note} />}
              <Field label="IP" value={entry.ipAddress ?? '—'} mono />
              <Field label="รหัสกิจกรรม" value={entry.action} mono />
            </dl>

            {entry.changes.length > 0 ? (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                  รายการที่เปลี่ยน ({entry.changes.length})
                </p>
                <div className="rounded-[var(--radius-sm)] border border-border divide-y divide-border">
                  {entry.changes.map((c, i) => (
                    <div key={`${c.betType}-${c.number ?? 'type'}-${i}`} className="px-2.5 py-1.5">
                      <ChangeChip change={c} highlight={highlight} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">{summarize(entry).fallback}</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-[10px] uppercase tracking-widest text-muted-foreground w-24 shrink-0 mt-0.5">
        {label}
      </dt>
      <dd className={`break-all ${mono ? 'font-mono text-foreground' : 'text-foreground'}`}>
        {value}
      </dd>
    </div>
  )
}

/**
 * One filter: its label above its control.
 *
 * A wrapper rather than a repeated pair because the label has to be forced to `block`.
 * A <label> is inline and a native <select> is inline-block, so without it the two sit
 * side by side while the fields built on <Input> stack — which is exactly how this
 * screen shipped with "หวย" and "ตั้งโดย" squashed against their dropdowns.
 */
function FilterField({
  htmlFor, label, children,
}: { htmlFor: string; label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="block text-xs font-normal text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  )
}

/** 08/10/2026 10:59:05 — Thai time, which is what the operator's clock says. */
function fullTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit',
  })
}

function shortTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

export function NumberLimitHistoryTable({
  fetchPage, lotteries, showScope = false, actors = null, subtitle, emptyHint,
}: Props) {
  const [draft, setDraft] = useState<Draft>(initialDraft)
  // `applied` is what the last search sent. Editing an input must not refetch on every
  // keystroke — a number half-typed is a different search.
  const [applied, setApplied] = useState<Draft>(initialDraft)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<PaginatedResult<NumberLimitHistoryEntry> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<NumberLimitHistoryEntry | null>(null)

  const query = useMemo(() => toQuery(applied), [applied])

  const load = useCallback(async (p: number, q: NumberLimitHistoryQuery) => {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchPage({ ...q, page: p }))
    } catch (e) {
      // The filters are kept on failure: losing a date range to a dropped request means
      // typing it again, and the request is the thing that failed, not the input.
      setError(e instanceof ApiRequestError ? e.message : 'โหลดประวัติไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [fetchPage])

  useEffect(() => { void load(page, query) }, [load, page, query])

  function search() {
    setPage(1)
    setApplied(draft)
  }

  function quickRange(days: number) {
    const next = { ...draft, from: ictDaysAgo(days), to: todayIct() }
    setDraft(next)
    setPage(1)
    setApplied(next)
  }

  function clearFilters() {
    const fresh = initialDraft()
    setDraft(fresh)
    setPage(1)
    setApplied(fresh)
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 0
  const narrowed = !!(applied.number || applied.lotteryId || applied.setBy)

  return (
    <div className="space-y-4">
      {/*
        One row of fields that wraps, then the two buttons on their own line.
        gap-x-5 rather than gap-3: at gap-3 a field's control sits as close to the next
        field's label as to its own, and the eye stops being able to tell which label
        belongs to which control.
      */}
      <section
        aria-label="ตัวกรองประวัติ"
        className="px-4 py-3.5 border border-border rounded-[var(--radius)] bg-card space-y-4"
      >
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <FilterField htmlFor="nlh-from" label="ตั้งแต่วันที่">
            <Input
              id="nlh-from" type="date" value={draft.from} max={draft.to || undefined}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className="h-9 w-40 font-mono text-sm"
            />
          </FilterField>
          <FilterField htmlFor="nlh-to" label="ถึงวันที่">
            <Input
              id="nlh-to" type="date" value={draft.to} min={draft.from || undefined}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              className="h-9 w-40 font-mono text-sm"
            />
          </FilterField>
          {/* The three ranges actually asked for, so the common case is one press. */}
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" className="h-9" onClick={() => quickRange(0)}>วันนี้</Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => quickRange(7)}>7 วัน</Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => quickRange(30)}>30 วัน</Button>
          </div>

          <FilterField htmlFor="nlh-number" label="เลข">
            <Input
              id="nlh-number" value={draft.number} placeholder="123 / สูง"
              maxLength={4}
              onChange={(e) => setDraft({ ...draft, number: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') search() }}
              className="h-9 w-28 font-mono text-sm"
            />
          </FilterField>

          {lotteries && lotteries.length > 0 && (
            <FilterField htmlFor="nlh-lottery" label="หวย">
              <select
                id="nlh-lottery" value={draft.lotteryId} className={`${SELECT_CLASS} w-48`}
                onChange={(e) => setDraft({ ...draft, lotteryId: e.target.value })}
              >
                <option value="">ทุกหวย</option>
                {lotteries.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </FilterField>
          )}

          {showScope && (
            <FilterField htmlFor="nlh-scope" label="ตั้งโดย">
              {/*
                One control, not two. "เอเย่นต์ทุกราย" and "เอเย่นต์ agent01" are the same
                axis at two depths — as separate controls they would look combinable, and
                picking both would mean nothing.
              */}
              <select
                id="nlh-scope" value={draft.setBy} className={`${SELECT_CLASS} w-56`}
                onChange={(e) => setDraft({ ...draft, setBy: e.target.value })}
              >
                <option value="">ทุกคน</option>
                {/* The two sides stay at the top level so filtering by side keeps
                    working while the named accounts are still loading. */}
                <option value="house">แอดมินทุกคน (ทั้งเว็บ)</option>
                <option value="agent">เอเย่นต์ทุกราย</option>
                {setByOptions(actors).map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </FilterField>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={search} className="h-9 gap-1.5">
            <Search className="h-4 w-4" aria-hidden />ค้นหา
          </Button>
          <Button variant="outline" onClick={clearFilters} className="h-9 gap-1.5">
            <X className="h-4 w-4" aria-hidden />ล้างตัวกรอง
          </Button>
        </div>
      </section>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
          <p className="text-xs text-destructive">{error}</p>
          <Button
            variant="outline" size="sm" className="h-7 ml-auto"
            onClick={() => void load(page, query)}
          >
            ลองใหม่
          </Button>
        </div>
      )}

      <section
        aria-label="ประวัติการอั้นเลข"
        className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]"
      >
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {subtitle}
          </span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">
              {data.total.toLocaleString('en-US')} รายการ
            </span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border" aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="ml-auto h-4 w-48" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 px-6 text-center">
            <History className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" aria-hidden />
            <p className="text-sm text-foreground">
              {narrowed
                ? 'ไม่พบประวัติที่ตรงกับตัวกรอง'
                : 'ยังไม่มีการอั้นเลขในช่วงวันที่เลือก'}
            </p>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-sm mx-auto">
              {narrowed ? 'ลองขยายช่วงวันที่ หรือล้างตัวกรอง' : emptyHint}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">เวลา</TableHead>
                  <TableHead className="w-28">ผู้ทำ</TableHead>
                  <TableHead className="w-40">กิจกรรม</TableHead>
                  {showScope && <TableHead className="w-32">มีผลกับ</TableHead>}
                  <TableHead className="w-40">หวย</TableHead>
                  <TableHead>รายการที่เปลี่ยน</TableHead>
                  <TableHead className="w-10 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((e) => {
                  const s = summarize(e)
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="align-top">
                        <time dateTime={e.createdAt} className="font-mono text-xs whitespace-nowrap">
                          <span className="text-foreground">{shortDate(e.createdAt)}</span>
                          <span className="block text-muted-foreground">{shortTime(e.createdAt)}</span>
                        </time>
                      </TableCell>
                      <TableCell className="align-top">
                        <span className="font-mono text-xs">
                          {e.actorUsername ?? e.actorId?.slice(-8) ?? 'system'}
                        </span>
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge variant={actionVariant(e.action)} className="text-[11px]">
                          {ACTION_LABEL[e.action]}
                        </Badge>
                      </TableCell>
                      {showScope && (
                        <TableCell className="align-top">
                          <span className="text-xs text-foreground">{whoseBook(e)}</span>
                        </TableCell>
                      )}
                      <TableCell className="align-top">
                        <span className="text-xs text-foreground block">
                          {e.lotteryName ?? e.lotteryCode ?? '—'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{scopeLabel(e)}</span>
                      </TableCell>
                      <TableCell className="align-top">
                        {s.fallback ? (
                          <span className="text-xs text-muted-foreground">{s.fallback}</span>
                        ) : (
                          <div className="space-y-0.5">
                            {s.shown.map((c, i) => (
                              <ChangeChip
                                key={`${c.betType}-${c.number ?? 'type'}-${i}`}
                                change={c}
                                highlight={applied.number.trim() || undefined}
                              />
                            ))}
                            {s.more > 0 && (
                              <span className="text-[10px] text-muted-foreground">
                                +{s.more} รายการ — กดดูรายละเอียด
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="align-top text-right">
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => setDetail(e)}
                          aria-label={`ดูรายละเอียด ${ACTION_LABEL[e.action]} ${shortDate(e.createdAt)} ${shortTime(e.createdAt)}`}
                        >
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">
              หน้า {page} / {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline" size="sm" className="h-7 w-7 p-0"
                disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="หน้าก่อน"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              </Button>
              <Button
                variant="outline" size="sm" className="h-7 w-7 p-0"
                disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="หน้าถัดไป"
              >
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
          </div>
        )}
      </section>

      <DetailDialog
        entry={detail} onClose={() => setDetail(null)} showScope={showScope}
        highlight={applied.number.trim() || undefined}
      />
    </div>
  )
}
